import { Core } from "cytoscape";
import { saveAs } from "file-saver";

import { UNAUTH_IMG_SCALE } from "../lib/constants";
import { cytoscape } from "../lib/cytoscape";
import { getBackground } from "../lib/toTheme";
import { ElementNode, parse } from "svg-parser";

// padding, gets divided in half
const PADDING = 60;

/**
 * Returns the SVG code for the current graph
 */
export async function getSvg({ cy }: { cy: Core }) {
  const bg = getBackground();

  // check if cy has loaded the svg plugin
  // @ts-ignore
  if (!cy.svg) {
    const cytoscapeSvg = await import("cytoscape-svg");
    cytoscape.use(cytoscapeSvg.default);
  }

  try {
    // @ts-ignore
    const svgStr = cy.svg({
      full: true,
      scale: 1.5,
      quality: 1,
      bg,
    });
    const domparser = new DOMParser();
    const svgEl = domparser.parseFromString(svgStr, "image/svg+xml");

    const pad = PADDING / 2;
    const svgTag = svgEl.children[0];
    const bgRect = svgTag.querySelector("rect");

    // add an attribute to recognize it
    if (bgRect) bgRect.setAttribute("data-bg", "");

    // create a group and move all svg children into it
    const group = svgEl.createElementNS("http://www.w3.org/2000/svg", "g");
    while (svgTag.firstChild) {
      group.appendChild(svgTag.firstChild);
    }
    svgTag.appendChild(group);
    group.setAttribute("transform", `translate(${pad}, ${pad})`);

    // update width to include padding
    const w = parseInt(svgTag.getAttribute("width") ?? "0", 10);
    const h = parseInt(svgTag.getAttribute("height") ?? "0", 10);
    const padWidth = w + PADDING;
    const padHeight = h + PADDING;
    svgTag.setAttribute("width", padWidth.toString());
    svgTag.setAttribute("height", padHeight.toString());

    if (bgRect) {
      // move bgRect out of group to the top level
      svgTag.insertBefore(bgRect, group);
      // make full size
      bgRect.setAttribute("width", padWidth.toString());
      bgRect.setAttribute("height", padHeight.toString());
    }

    const correctedSvgStr = svgEl.documentElement.outerHTML;
    const { optimize } = await import("svgo/dist/svgo.browser");

    const { data } = optimize(correctedSvgStr, {
      js2svg: { pretty: true, indent: 2 },
      plugins: ["removeDimensions"],
    });
    return data;
  } catch (e) {
    console.error(e);
    return "";
  }
}

export async function downloadSvg({
  svg,
  filename,
}: {
  svg: string;
  filename: string;
}) {
  return saveAs(
    new Blob([svg], {
      type: "image/svg+xml;charset=utf-8",
    }),
    `${filename}.svg`
  );
}

/**
 * Grab the current graph on a canvas with padding
 * and possible watermark for conversion and download
 */
export async function getCanvas({
  cy,
  type,
  scale = UNAUTH_IMG_SCALE,
  watermark = true,
}: {
  cy: Core;
  type: "jpg" | "png";
  scale?: number;
  watermark?: boolean;
}): Promise<{
  canvas: HTMLCanvasElement;
  type: "jpg" | "png";
  cleanup: () => void;
}> {
  const bg = getBackground();
  const blob = await cy[type]({
    full: true,
    scale,
    output: "blob-promise",
    bg,
  });

  // Get width and height of flowchart
  const { w, h } = await new Promise<{ w: number; h: number }>((resolve) => {
    try {
      const img = new Image();
      img.src = window.URL.createObjectURL(blob);
      let w: number, h: number;
      img.onload = () => {
        w = img.naturalWidth;
        h = img.naturalHeight;
        document.body.removeChild(img);
        resolve({ w, h });
      };
      document.body.appendChild(img);
    } catch (e) {
      resolve({ w: 0, h: 0 });
    }
  });

  // Create canvas with size + padding
  const canvas = document.createElement("canvas");
  canvas.width = w + PADDING;
  canvas.height = h + PADDING;

  // add canvas to document and get context
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  // draw background on canvas
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // take the blob and draw it on center of canvas
  const img = new Image();
  img.src = window.URL.createObjectURL(blob);
  return new Promise((resolve) => {
    img.onload = () => {
      ctx.drawImage(img, PADDING / 2, PADDING / 2);
      window.URL.revokeObjectURL(img.src);
      // add watermark
      if (watermark)
        addWatermark({
          ctx,
          width: canvas.width,
          height: canvas.height,
        });
      resolve({
        canvas,
        type,
        cleanup: () => {
          // throw away canvas
          canvas.remove();
        },
      });
    };
  });
}

export async function svgToCanvas({
  svg,
  type,
  scale = UNAUTH_IMG_SCALE,
  watermark = true,
}: {
  svg: string;
  type: "jpg" | "png";
  scale?: number;
  watermark?: boolean;
}): Promise<{
  canvas: HTMLCanvasElement;
  type: "jpg" | "png";
  cleanup: () => void;
}> {
  const bg = getBackground();

  // Some diagram got issue `namespace prefix xlink for href on image is not defined`
  // Ref: https://github.com/mermaid-js/mermaid/issues/4092
  if (!svg.includes(`xmlns:xlink="http://www.w3.org/1999/xlink"`)) {
    svg = svg.replace(
      `xmlns="http://www.w3.org/2000/svg"`,
      `xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"`
    );
  }
  const svgParsed = parse(svg);
  const svgElement = svgParsed.children[0] as ElementNode;
  const viewBox = svgElement.properties?.viewBox ?? "0 0 0 0";

  // Get width and height of flowchart
  const { w, h } = await new Promise<{ w: number; h: number }>((resolve) => {
    try {
      const img = new Image();
      img.src = buildSvgImageUrl(svg);
      let w: number, h: number;

      img.onload = () => {
        [w, h] = parseViewBox(viewBox as string);

        if (w == 0 || h == 0) {
          w = img.naturalWidth;
          h = img.naturalHeight;
        }

        console.log(w, h);

        document.body.removeChild(img);
        resolve({ w, h });
      };
      document.body.appendChild(img);
    } catch (e) {
      console.log(e);
      resolve({ w: 0, h: 0 });
    }
  });

  // Create canvas with size + padding
  const canvas = document.createElement("canvas");
  canvas.width = w + PADDING;
  canvas.height = h + PADDING;

  // add canvas to document and get context
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  // draw background on canvas
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // take the blob and draw it on center of canvas
  const img = new Image();
  img.src = buildSvgImageUrl(svg);
  return new Promise((resolve) => {
    img.onload = () => {
      ctx.drawImage(img, PADDING / 2, PADDING / 2);
      window.URL.revokeObjectURL(img.src);
      // add watermark
      if (watermark)
        addWatermark({
          ctx,
          width: canvas.width,
          height: canvas.height,
        });
      resolve({
        canvas,
        type,
        cleanup: () => {
          // throw away canvas
          canvas.remove();
        },
      });
    };
  });
}

function parseViewBox(viewBoxString: string): [number, number] {
  const parts = viewBoxString.split(" ");
  const width = parseFloat(parts[2]);
  const height = parseFloat(parts[3]);

  return [width, height];
}

function buildSvgImageUrl(svg: string) {
  const b64 = window.btoa(unescape(encodeURIComponent(svg)));
  return "data:image/svg+xml;base64," + b64;
}

async function addWatermark({
  ctx,
  width,
  height,
}: {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
}) {
  // get a size that is 3% of the canvas height
  const heightRelativeSize = Math.floor(height * 0.03);
  const widthRelativeSize = Math.floor(width * 0.05);
  // take the smaller of the two
  const size = Math.min(heightRelativeSize, widthRelativeSize);
  ctx.font = `${Math.floor(size)}px Helvetica`;
  ctx.fillStyle = "#000000";
  ctx.fillText("diagram.tdadevelop.com", 5, height - size / 2);
}

export function downloadCanvas({
  canvas,
  type,
  cleanup,
  filename,
}: {
  filename: string;
} & Awaited<ReturnType<typeof getCanvas>>) {
  const mime = type === "png" ? "image/png" : "image/jpeg";
  saveAs(canvas.toDataURL(mime), `${filename}.${type}`);
  cleanup();
}

export async function copyCanvas(props: Awaited<ReturnType<typeof getCanvas>>) {
  const { canvas, cleanup, type } = props;
  if (type !== "png") throw new Error("Can only copy png images");
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });
  if (!blob) return;
  cleanup();
  return navigator.clipboard.write([
    new ClipboardItem({
      [`image/png`]: blob,
    }),
  ]);
}
